import plaidActions from 'core/plaid/actions';
import dialogActions from 'core/dialogs/actions';
import pageActions from '../page/actions';
import groupActions from '../groups/actions';
import fieldActions from './actions';
import dataActions from '../data/actions';

const createLookupField = ({ label, lookupValues, requiredErrorMsg }) => ({
  productLookup: {
    clientId: 'productLookup',
    columnSize: 4,
    error: false,
    errorMsg: null,
    fieldType: 'LOOKUP',
    id: 1,
    label,
    lookupValues,
    required: true,
    requiredErrorMsg,
    value: null,
  },
});

export default (state = {}, action) => {
  switch (action.type) {
    case plaidActions.FETCH_ACCOUNTS_SUCCESS:
    case plaidActions.HANDLE_VERIFICATION_QUESTIONS:
    case dialogActions.SEND_CODE_SUCCESS:
    case groupActions.UPDATE_SUCCESS:
    case pageActions.GO_SUCCESS: {
      const {
        data = {},
        meta: { fields, groups, page },
      } = action.payload.data;
      const dataLists = data && Object.keys(data).filter((dataKey) => dataKey.includes('___list'));
      const groupsWithLists = Object.keys(data)
        .filter((dataKey) => dataKey.includes('___list'))
        .map((listName) => listName.split('___list')[0]);

      /** HACK: This should be removed when the prod start page
       * is replaced with the product showcase
       */
      if (page?.name === 'prodstart') {
        const prodStartGroupResources = action.payload.data.meta.groups.PROD_START?.resources;
        return createLookupField({
          label: prodStartGroupResources.selectLabel,
          lookupValues: data.products.reduce(
            (obj, { label, name }) => ({ ...obj, [name]: label }),
            {},
          ),
          requiredErrorMsg: prodStartGroupResources.requiredErrorMsg,
        });
      }
      if (data && data.questions) {
        const fieldProps = {
          columnSize: 4,
          error: false,
          errorMsg: null,
          fieldType: 'LOOKUP',
          layout: 'radioVertical',
          required: true,
          value: null,
        };
        return data.questions.reduce((newState, question) => {
          const clientId = `KBA_questions${question.id}`;
          return {
            ...newState,
            [clientId]: {
              ...fieldProps,
              clientId,
              id: question.id,
              label: question.value,
              lookupValues: question.answers.reduce(
                (acc, curr) => ({
                  ...acc,
                  [curr.id]: curr.value,
                }),
                {},
              ),
            },
          };
        }, {});
      }
      if (dataLists && dataLists.length > 0) {
        const fieldsInListGroups = groupsWithLists
          ?.map((groupName) => groups[groupName].fields)
          ?.flat()
          ?.filter((fieldName, i, arr) => arr.indexOf(fieldName) === i);
        const remainingFields = Object.keys(fields)
          ?.filter((fieldName) => !fieldsInListGroups.includes(fieldName))
          ?.reduce((acc, fieldName) => ({ ...acc, [fieldName]: fields[fieldName] }), {});

        return dataLists.reduce((obj, dataListKey) => {
          const fieldData = data?.[dataListKey].length ? data?.[dataListKey] : [{}];

          return {
            ...obj,
            ...remainingFields,
            ...fieldData.reduce(
              (fieldObj, _key, i) => ({
                ...fieldObj,
                ...fieldsInListGroups.reduce(
                  (newFields, fieldKey) => ({
                    ...newFields,
                    [`${fieldKey}:${i}`]: fields[fieldKey],
                  }),
                  {},
                ),
              }),
              {},
            ),
          };
        }, {});
      }

      return fields || {};
    }
    case pageActions.UPDATE_PAGE_DATA: {
      const { data } = action.payload;
      return Object.keys(data.meta.fields).reduce((fields, fieldName) => {
        const field = data.meta.fields[fieldName];
        return {
          ...fields,
          [fieldName]: {
            ...field,
          },
        };
      }, {});
    }
    case fieldActions.RERENDER_SUCCESS:
    case groupActions.UPLOAD_SUCCESS: {
      const { data, forceUpdate } = action.payload;
      return Object.keys(data.meta.fields).reduce((fields, fieldName) => {
        const field = data.meta.fields[fieldName];
        return {
          ...fields,
          [fieldName]: {
            ...field,
            error:
              forceUpdate || field.hidden
                ? field.error
                : field.error || (state[fieldName] && state[fieldName].error) || false,
            errorMsg:
              forceUpdate || field.hidden
                ? field.errorMsg
                : field.errorMsg || (state[fieldName] && state[fieldName].errorMsg) || null,
          },
        };
      }, {});
    }
    case fieldActions.RERENDER_FAILED:
    case pageActions.NEXT_FAILED:
    case groupActions.UPLOAD_FAILED:
    case groupActions.UPDATE_FAILED:
    case pageActions.VERIFY_FUNDING_FAILED:
    case groupActions.NEXT_FAILED: {
      const { data } = action.payload;
      const response = data.result || data.meta;
      return {
        ...state,
        ...(data.meta && data.meta.fields),
        ...Object.keys(response.msgs).reduce((newFields, key) => {
          if (state[key]) {
            return {
              ...newFields,
              [key]: {
                ...state[key],
                ...data?.meta?.fields[key],
                error: true,
                errorMsg: response.msgs[key],
              },
            };
          }
          return newFields;
        }, {}),
      };
    }
    case groupActions.NEXT_SUCCESS: {
      // Clear any leftover validation errors
      return {
        ...state,
        ...Object.keys(state).reduce(
          (acc, key) => ({
            ...acc,
            [key]: {
              ...state[key],
              error: false,
              errorMsg: '',
            },
          }),
          {},
        ),
      };
    }
    case fieldActions.FETCH_AUTOCOMPLETE_SUCCESS:
      return {
        ...state,
        [action.payload.fieldName]: {
          ...state[action.payload.fieldName],
          selectorValues: action.payload.data.data,
        },
      };
    case fieldActions.VALIDATE_SUCCESS:
      return {
        ...state,
        [action.payload.fieldName]: {
          ...state[action.payload.fieldName],
          error: false,
          errorMsg: null,
        },
      };
    case fieldActions.SET_ERROR:
      return {
        ...state,
        [action.payload.fieldName]: {
          ...state[action.payload.fieldName],
          error: true,
          errorMsg: action.payload.errorMsg,
        },
      };
    case dataActions.CHANGE_AUTO_COMPLETE: {
      const field = state[action.payload.key];

      return {
        ...state,
        [action.payload.key]: {
          ...field,
          selectorValues: action.payload.option && [action.payload.option],
        },
      };
    }
    case fieldActions.RECAPTCHA_SUCCESS: {
      const key = action.payload.fieldName;
      return {
        ...state,
        [key]: {
          ...state[key],
          error: false,
          errorMsg: '',
        },
      };
    }
    default:
      return state;
  }
};
